// src/components/common/vibe/PostCard.js
import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Bookmark, BookmarkCheck, MoreVertical, Trash2, Edit2, Pin, EyeOff, Shield, Lightbulb } from 'lucide-react';
import { MOODS, REACTIONS, formatTimeAgo, getMoodData } from '../../../constants/vibeConstants';
import ReactionBar from './ReactionBar';
import CommentsSection from './CommentsSection';
import PollCard from './PollCard';
import EditPost from './EditPost';
import ConfettiEffect from './ConfettiEffect';
import { useEmployees } from '../../../contexts/EmployeeContext';
import vibeService from '../../../services/vibe.service';

const PostCard = ({ post, currentUser, onReaction, onComment, onDelete, onSave, onEdit, theme }) => {
    const [showComments, setShowComments] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [showReactions, setShowReactions] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);
    const { employees } = useEmployees();
    const hasShownInitialConfetti = useRef(false);

    // Debug: Log post data to understand image structure
    console.log('📸 PostCard - Post data:', {
        postId: post.id,
        post_category: post.post_category,
        post_type: post.post_type,
        image_url: post.image_url,
        image_urls: post.image_urls,
        hasImages: !!(post.image_url || post.image_urls),
        isSuggestionPost: post.post_type === 'suggestions',
        suggestionDetection: {
            post_category: post.post_category,
            post_type: post.post_type,
            is_suggestions_by_category: post.post_category === 'suggestions',
            is_suggestions_by_type: post.post_type === 'suggestions',
            category_type: typeof post.post_category,
            type_type: typeof post.post_type
        },
        allData: post
    });

    const isOwnPost = post.employee_id === currentUser?.id;
    const isAdmin = ['Super Admin', 'HR Admin'].includes(currentUser?.role);
    const moodData = post.mood ? getMoodData(post.mood) : null;
    const isKudosPost = post.post_category === 'kudos';
    const isSuggestionPost = post.post_type === 'suggestions';

    // Detect celebration posts by post_type or content prefix
    const isCelebrationPost = post.post_type === 'celebration' ||
                             post.post_category === 'birthday_celebration' ||
                             post.post_category === 'work_anniversary_celebration' ||
                             post.is_celebration_post ||
                             (post.content && (post.content.includes('[BIRTHDAY_CELEBRATION]') || post.content.includes('[WORK_ANNIVERSARY]')));

    // DEBUG: Log celebration post detection
    console.log('🎉 PostCard - Post data:', {
        postId: post.id,
        post_type: post.post_type,
        post_category: post.post_category,
        is_celebration_post: post.is_celebration_post,
        content_prefix: post.content?.substring(0, 50),
        isCelebrationPost,
        created_at: post.created_at,
        postAge: Date.now() - new Date(post.created_at).getTime()
    });

    // Show confetti for newly created celebration posts (within 5 seconds)
    useEffect(() => {
        console.log('🎊 PostCard useEffect - Confetti check:', {
            isCelebrationPost,
            hasShownInitialConfetti: hasShownInitialConfetti.current,
            postAge: Date.now() - new Date(post.created_at).getTime()
        });

        if (isCelebrationPost && !hasShownInitialConfetti.current) {
            const postAge = Date.now() - new Date(post.created_at).getTime();
            console.log('✅ PostCard - Celebration post detected! Post age:', postAge, 'ms');

            // If post is less than 5 seconds old, show confetti
            if (postAge < 5000) {
                console.log('🎉 PostCard - Showing confetti!');
                setShowConfetti(true);
                hasShownInitialConfetti.current = true;
                setTimeout(() => {
                    console.log('🎉 PostCard - Hiding confetti');
                    setShowConfetti(false);
                }, 3000);
            } else {
                console.log('⏰ PostCard - Post too old for confetti:', postAge, 'ms');
            }
        }
    }, [isCelebrationPost, post.created_at]);

    // If this is a poll, render PollCard instead
    if (post.is_poll) {
        return <PollCard post={post} currentUser={currentUser} onVoted={onComment} theme={theme} />;
    }

    // Get tagged employees for kudos posts
    const getTaggedEmployees = () => {
        if (!isKudosPost || !post.mentioned_employees || post.mentioned_employees.length === 0) {
            return [];
        }
        return post.mentioned_employees.map(empId => {
            const emp = employees.find(e => e.id === empId);
            return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown';
        });
    };

    const taggedEmployees = getTaggedEmployees();

    const handleEditPost = async () => {
        setShowEditModal(true);
        setShowMenu(false);
    };

    const handlePostUpdated = async () => {
        if (onEdit) {
            await onEdit();
        }
    };

    const handleReactionClick = async (reactionType) => {
        try {
            await onReaction(post.id, reactionType);
            setShowReactions(false);

            // Trigger confetti for celebration posts
            if (isCelebrationPost) {
                console.log('🎉 PostCard - Reaction on celebration post! Showing confetti');
                setShowConfetti(true);
                setTimeout(() => {
                    console.log('🎉 PostCard - Hiding confetti after reaction');
                    setShowConfetti(false);
                }, 3000);
            }
        } catch (error) {
            console.error('Error adding reaction:', error);
        }
    };

    const handleAdminHide = async () => {
        if (!window.confirm('Hide this post from everyone\'s feed? (Only admins can see hidden posts)')) return;
        try {
            await vibeService.moderatePost(post.id, {
                moderation_action: 'hide',
                moderation_reason: 'Hidden by admin'
            });
            setShowMenu(false);
            if (onEdit) await onEdit(); // Refresh the feed
        } catch (error) {
            console.error('Error hiding post:', error);
            alert('Failed to hide post');
        }
    };

    const handleAdminDelete = async () => {
        if (!window.confirm('Permanently delete this post? This action cannot be undone.')) return;
        try {
            await vibeService.moderatePost(post.id, {
                moderation_action: 'delete',
                moderation_reason: 'Deleted by admin'
            });
            setShowMenu(false);
            if (onEdit) await onEdit(); // Refresh the feed
        } catch (error) {
            console.error('Error deleting post:', error);
            alert('Failed to delete post');
        }
    };

    return (
        <>
            {/* Confetti Effect for celebration posts */}
            {isCelebrationPost && <ConfettiEffect trigger={showConfetti} duration={3000} />}

            {/* Edit Post Modal */}
            <EditPost
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                post={post}
                onPostUpdated={handlePostUpdated}
                theme={theme}
            />

            <div className={`rounded-xl p-6 border relative ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
                {/* Saved Icon Badge */}
                {post.is_saved && (
                    <div className="absolute top-3 left-3">
                        <BookmarkCheck className={`w-5 h-5 ${
                            theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                    </div>
                )}

                {/* Post Header */}
                <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-3">
                    {/* Author Avatar */}
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                        theme === 'dark' ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-600'
                    }`}>
                        {post.author_name?.[0] || 'U'}
                    </div>

                    {/* Author Info */}
                    <div>
                        <div className="flex items-center gap-2">
                            <h4 className={`font-semibold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                {post.author_name}
                            </h4>
                            {post.is_pinned && (
                                <Pin className="w-4 h-4 text-blue-600" />
                            )}
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                            <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                {post.author_designation}
                            </span>
                            <span className={theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}>•</span>
                            <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}>
                                {formatTimeAgo(post.created_at)}
                            </span>
                            {post.is_edited && (
                                <>
                                    <span className={theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}>•</span>
                                    <span className={theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}>
                                        edited
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Actions Menu */}
                <div className="relative">
                    <button
                        onClick={() => setShowMenu(!showMenu)}
                        className={`p-2 rounded-lg transition-colors ${
                            theme === 'dark'
                                ? 'hover:bg-gray-700 text-gray-400'
                                : 'hover:bg-gray-100 text-gray-600'
                        }`}
                    >
                        <MoreVertical className="w-5 h-5" />
                    </button>

                    {showMenu && (
                        <div className={`absolute right-0 mt-2 w-48 rounded-lg shadow-lg border z-10 ${
                            theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                            <button
                                onClick={() => {
                                    onSave(post.id);
                                    setShowMenu(false);
                                }}
                                className={`w-full px-4 py-2 text-left flex items-center gap-2 ${
                                    theme === 'dark'
                                        ? 'hover:bg-gray-700 text-gray-300'
                                        : 'hover:bg-gray-50 text-gray-700'
                                }`}
                            >
                                {post.is_saved ? (
                                    <BookmarkCheck className="w-4 h-4" />
                                ) : (
                                    <Bookmark className="w-4 h-4" />
                                )}
                                {post.is_saved ? 'Unsave' : 'Save'}
                            </button>
                            {isOwnPost && (
                                <>
                                    <button
                                        onClick={handleEditPost}
                                        className={`w-full px-4 py-2 text-left flex items-center gap-2 ${
                                            theme === 'dark'
                                                ? 'hover:bg-gray-700 text-gray-300'
                                                : 'hover:bg-gray-50 text-gray-700'
                                        }`}
                                    >
                                        <Edit2 className="w-4 h-4" />
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => {
                                            onDelete(post.id);
                                            setShowMenu(false);
                                        }}
                                        className="w-full px-4 py-2 text-left flex items-center gap-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Delete
                                    </button>
                                </>
                            )}
                            {isAdmin && !isOwnPost && (
                                <>
                                    <div className={`border-t ${
                                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                                    }`} />
                                    <div className={`px-3 py-1 flex items-center gap-2 ${
                                        theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                                    }`}>
                                        <Shield className="w-3 h-3" />
                                        <span className="text-xs font-medium">Admin Actions</span>
                                    </div>
                                    <button
                                        onClick={handleAdminHide}
                                        className={`w-full px-4 py-2 text-left flex items-center gap-2 ${
                                            theme === 'dark'
                                                ? 'hover:bg-gray-700 text-yellow-400'
                                                : 'hover:bg-yellow-50 text-yellow-700'
                                        }`}
                                    >
                                        <EyeOff className="w-4 h-4" />
                                        Hide Post
                                    </button>
                                    <button
                                        onClick={handleAdminDelete}
                                        className="w-full px-4 py-2 text-left flex items-center gap-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Delete Post (Admin)
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Mood Badge */}
            {moodData && (
                <div className="mb-3">
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${moodData.color}`}>
                        <span className="text-lg">{moodData.emoji}</span>
                        Feeling {moodData.label}
                    </span>
                </div>
            )}

            {/* Suggestion Badge */}
            {(() => {
                console.log('🔍 Checking suggestion badge:', {
                    isSuggestionPost,
                    post_category: post.post_category,
                    post_type: post.post_type,
                    condition: isSuggestionPost,
                    shouldRender: isSuggestionPost === true
                });
                return isSuggestionPost;
            })() && (
                <div className="mb-3">
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                        theme === 'dark' ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                        <Lightbulb className="w-4 h-4" />
                        Suggestion
                    </div>
                </div>
            )}

            {/* Kudos Badge - Tagged Employees */}
            {isKudosPost && taggedEmployees.length > 0 && (
                <div className="mb-3">
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                        theme === 'dark' ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                        <span className="text-lg">🏆</span>
                        Kudos to: {taggedEmployees.join(', ')}
                    </div>
                </div>
            )}

            {/* Post Content - Only show if there's actual content */}
            {post.content && post.content.trim() !== '' && (
                <div className={`mb-4 ${
                    theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
                }`}>
                    <p className="whitespace-pre-wrap">{post.content}</p>
                </div>
            )}

            {/* Images (if any) */}
            {(post.image_url || post.image_urls) && (
                <div className="mb-4">
                    {post.image_urls && post.image_urls.length > 0 ? (
                        // Multiple images
                        <div className={`grid gap-2 ${
                            post.image_urls.length === 1 ? 'grid-cols-1' :
                            post.image_urls.length === 2 ? 'grid-cols-2' :
                            'grid-cols-3'
                        }`}>
                            {post.image_urls.map((imageUrl, index) => (
                                <div key={index} className="rounded-lg overflow-hidden">
                                    <img
                                        src={imageUrl}
                                        alt={`Post image ${index + 1}`}
                                        className="w-full h-auto object-cover"
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        // Single image (legacy)
                        <div className="rounded-lg overflow-hidden">
                            <img
                                src={post.image_url}
                                alt="Post image"
                                className="w-full h-auto"
                            />
                        </div>
                    )}
                </div>
            )}

            {/* GIF (if any) */}
            {post.gif_url && (
                <div className="mb-4 rounded-lg overflow-hidden">
                    <img
                        src={post.gif_url}
                        alt={post.gif_title || 'GIF'}
                        className="w-full max-w-md h-auto"
                    />
                </div>
            )}

            {/* Reactions Summary */}
            {post.reactions_summary && post.reactions_summary.length > 0 && (
                <div className={`flex items-center gap-2 mb-3 pb-3 border-b ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    <div className="flex items-center -space-x-1">
                        {post.reactions_summary.slice(0, 3).map((reaction, index) => {
                            const reactionData = REACTIONS.find(r => r.type === reaction.reaction_type);
                            return (
                                <span
                                    key={index}
                                    className={`inline-flex items-center justify-center w-6 h-6 rounded-full border-2 ${
                                        theme === 'dark' ? 'border-gray-800 bg-gray-700' : 'border-white bg-gray-100'
                                    }`}
                                    title={`${reaction.count} ${reactionData?.label}`}
                                >
                                    {reactionData?.emoji}
                                </span>
                            );
                        })}
                    </div>
                    <span className={`text-sm ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                        {post.reactions_count} {post.reactions_count === 1 ? 'reaction' : 'reactions'}
                    </span>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
                {/* React Button with Picker */}
                <div className="relative flex-1">
                    <button
                        onClick={() => setShowReactions(!showReactions)}
                        className={`w-full py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                            post.has_reacted
                                ? 'bg-blue-600 text-white'
                                : theme === 'dark'
                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        <span className="text-lg">👍</span>
                        React
                    </button>

                    {showReactions && (
                        <ReactionBar
                            reactions={REACTIONS}
                            onReactionClick={handleReactionClick}
                            theme={theme}
                        />
                    )}
                </div>

                {/* Comment Button */}
                <button
                    onClick={() => setShowComments(!showComments)}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                        theme === 'dark'
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    <MessageCircle className="w-5 h-5" />
                    Comment {post.comments_count > 0 && `(${post.comments_count})`}
                </button>
            </div>

            {/* Comments Section */}
            {showComments && (
                <CommentsSection
                    postId={post.id}
                    currentUser={currentUser}
                    onCommentAdded={() => {
                        // Trigger confetti for celebration posts when comment is added
                        if (isCelebrationPost) {
                            console.log('🎉 PostCard - Comment on celebration post! Showing confetti');
                            setShowConfetti(true);
                            setTimeout(() => {
                                console.log('🎉 PostCard - Hiding confetti after comment');
                                setShowConfetti(false);
                            }, 3000);
                        }
                        // Call parent callback
                        if (onComment) onComment();
                    }}
                    theme={theme}
                />
            )}
            </div>
        </>
    );
};

export default PostCard;
